import { html, LitElement } from 'lit';
import { customElement } from 'lit/decorators.js';
// ?inline bypasses the litScssPlugin and gives the compiled CSS as a string.
// `static styles` is not usable here: this element renders in light DOM, and
// Lit only adopts `static styles` into a ShadowRoot. Instead we adopt the sheet
// onto whichever root this element lives in (see `_adoptStyles`), so the
// descendant selectors work both in the document and inside a host component's
// shadow root.
import stylesText from './vm-link-overlay.scss?inline';

const sheet = new CSSStyleSheet();
sheet.replaceSync(stylesText as string);

/**
 * Card container with a full-cover primary link.
 *
 * Renders in light DOM so secondary links at any nesting depth are elevated
 * above the overlay automatically — no `position: relative; z-index: 1`
 * needed on wrapper elements.
 *
 * Works inside another web component's shadow DOM: on connect it adopts its
 * stylesheet onto its containing root (`getRootNode()`), since `document`'s
 * adopted sheets don't cross shadow boundaries. Consumers therefore don't need
 * to re-declare the overlay/elevation rules in their own shadow stylesheet.
 *
 * The element slotted into `slot="link-overlay"` is stretched to cover the
 * entire host at z-index 0. All other descendant `<a href>` and `<abbr title>`
 * elements are elevated to z-index 1.
 *
 * @slot link-overlay - The primary anchor. Should be an `<a href>`. Covers the
 *                      full card. Use `aria-label` for the accessible name.
 * @slot (default)    - Card content at any depth. Descendant `<a href>` and
 *                      `<abbr title>` are elevated above the overlay automatically.
 *
 * @example
 * <vm-link-overlay>
 *   <a slot="link-overlay" href="/video/123" aria-label="Watch TAVR advances"></a>
 *   <img src="..." alt="" />
 *   <h2>TAVR Advances</h2>
 *   <p>Published by <a href="/channel/cardiology">Cardiology</a></p>
 * </vm-link-overlay>
 */
@customElement('vm-link-overlay')
export class VmLinkOverlay extends LitElement {
    override createRenderRoot() {
        return this;
    }

    override connectedCallback() {
        super.connectedCallback();
        this._adoptStyles();
        this.dataset.testid = 'vm-link-overlay';
    }

    // Adopt our styles into the containing root. `getRootNode()` is the document
    // when we're in light DOM, or the host component's shadow root when nested —
    // and the same sheet instance can be shared across many roots. Deduped by
    // identity so reconnects and sibling instances don't stack duplicates.
    private _adoptStyles() {
        const root = this.getRootNode() as Document | ShadowRoot;
        if ('adoptedStyleSheets' in root && !root.adoptedStyleSheets.includes(sheet)) {
            root.adoptedStyleSheets = [...root.adoptedStyleSheets, sheet];
        }
    }

    override render() {
        return html`
            <slot name="link-overlay"></slot>
            <slot></slot>
        `;
    }
}

declare global {
    interface HTMLElementTagNameMap {
        'vm-link-overlay': VmLinkOverlay;
    }
}
